// Gallery — grid of recent generations + click-to-open detail modal.
// The detail modal renders the full image, prompt, settings rollup,
// and a Delete action (themed confirm). Inpaint + variations are
// out of scope for Phase 1.

import { useEffect } from "preact/hooks";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import type { GalleryItem } from "../../lib/rpc";

interface GalleryProps {
  items: GalleryItem[];
  loading: boolean;
  onOpen: (item: GalleryItem) => void;
}

export function Gallery({ items, loading, onOpen }: GalleryProps) {
  if (loading && items.length === 0) {
    return <div className="studio-gallery-empty">Loading…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="studio-gallery-empty">
        <Icon name="image" size={32} />
        <p>Nothing here yet. Imagine something first.</p>
      </div>
    );
  }
  return (
    <div className="studio-gallery-grid">
      {items.map((item) => (
        <button
          key={item.id}
          className="studio-gallery-tile"
          onClick={() => onOpen(item)}
          type="button"
        >
          <img src={`/api/gallery-img/${item.id}`} alt={item.prompt} loading="lazy" />
          {item.settings?.inpaint && <span className="studio-gallery-badge" title="Inpainted">✎</span>}
        </button>
      ))}
    </div>
  );
}

interface GalleryDetailProps {
  item: GalleryItem;
  onClose: () => void;
  onDelete: () => void;
  onInpaint: () => void;
}

export function GalleryDetail({ item, onClose, onDelete, onInpaint }: GalleryDetailProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Delete this generation?",
      message: "The image will be removed from your gallery and disk. Can't be undone.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    onDelete();
  };

  const settings = item.settings || {};
  const settingRow = (label: string, value: unknown) => {
    if (value === undefined || value === null || value === "") return null;
    return (
      <div className="studio-detail-setting">
        <span className="studio-detail-setting-label">{label}</span>
        <span className="studio-detail-setting-value">{String(value)}</span>
      </div>
    );
  };

  return (
    <div className="studio-modal-bg" onClick={onClose}>
      <div className="studio-modal" onClick={(e) => e.stopPropagation()}>
        <div className="studio-modal-img">
          <img src={`/api/gallery-img/${item.id}`} alt={item.prompt} />
        </div>
        <div className="studio-modal-side">
          <div className="studio-modal-head">
            <div className="eyebrow">Prompt</div>
            <button className="icon-btn" onClick={onClose} type="button" title="Close">
              <Icon name="x" size={14} />
            </button>
          </div>
          <p className="studio-modal-prompt">"{item.prompt}"</p>
          {item.negative && (
            <>
              <div className="eyebrow" style={{ marginTop: 14 }}>Negative</div>
              <p className="studio-modal-negative">{item.negative}</p>
            </>
          )}
          <div className="eyebrow" style={{ marginTop: 14 }}>Settings</div>
          <div className="studio-modal-settings">
            {settingRow("Style",   settings.style)}
            {settingRow("Quality", settings.quality)}
            {settingRow("Aspect",  settings.aspect)}
            {settingRow("Steps",   settings.steps)}
            {settingRow("CFG",     settings.cfg)}
            {settingRow("Sampler", settings.sampler)}
            {settingRow("Seed",    settings.seed)}
            {settings.inpaint && settingRow("Mode", "Inpaint")}
          </div>
          <div className="studio-modal-actions">
            <button className="btn btn-primary" onClick={onInpaint} type="button">
              <Icon name="edit" size={13} /> Inpaint
            </button>
            <button className="btn" onClick={handleDelete} type="button">
              <Icon name="trash" size={13} /> Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
